/**
 * Editing a node's pins (a blueprint's ways out, a scene's choices), the same from the blueprint
 * and from the inspector: add one ("Choice n"), rename it, wire it to a node (a link from that
 * pin, of the kind the environment plays; a pin holds one wire, so wiring it again moves its
 * wire), disconnect it, remove it (its wire goes with it, in the same undo). Every change is one
 * edit through `actions`; the core checks it.
 */
import type { Pin } from "@protocol/Pin";
import type { Actions } from "../client/actions";
import type { GraphMirror } from "../client/graph";
import { toast } from "../shared/toast";
import { nextPinId, wireFrom } from "./diagram";
import type { KindsService } from "./kinds";
import type { Play } from "./play";
import type { View } from "./view";

/** What the pin edits need. */
export interface PinEditDeps {
  graph: Pick<GraphMirror, "state">;
  actions: Pick<Actions, "updateNode" | "updateLink" | "removeLink">;
  kinds: Pick<KindsService, "wire" | "addLink">;
  /** What the environment plays (its choice kinds are the wires' kinds). */
  play: Pick<Play, "def">;
  view: Pick<View, "showLinkKind">;
}

/** The pin edits. Each answers whether the core took it. */
export interface PinEdits {
  /** The node's pins now. */
  pinsOf(node: string): Pin[];
  /** The words of a pin: its label, else the name of where it leads, else its id (as Play words the choice). */
  label(node: string, pin: Pin): string;
  /** Adds a pin at the end, worded "Choice n". */
  add(node: string): Promise<boolean>;
  /** Changes a pin's words. */
  rename(node: string, pin: string, label: string): Promise<boolean>;
  /** Wires a pin to node `to` (moving its wire there when it has one). */
  wire(node: string, pin: string, to: string): Promise<boolean>;
  /** Takes away a pin's wire. */
  disconnect(node: string, pin: string): Promise<boolean>;
  /** Removes a pin and its wire. */
  remove(node: string, pin: string): Promise<boolean>;
}

/** Creates the pin edits. */
export function pinEdits({ graph, actions, kinds, play, view }: PinEditDeps): PinEdits {
  const nodeOf = (id: string) => graph.state.graph.nodes.find((n) => n.id === id);
  const pinsOf = (id: string): Pin[] => nodeOf(id)?.pins ?? [];
  const setPins = async (id: string, pins: Pin[]) => !!(await actions.updateNode(id, { pins }));
  const edits: PinEdits = {
    pinsOf,
    label(node, pin) {
      if (pin.label.trim()) return pin.label.trim();
      const to = wireFrom(graph.state.graph.links, node, pin.id)?.to;
      return (to && nodeOf(to)?.name) || pin.id;
    },
    add(node) {
      const pins = pinsOf(node);
      return setPins(node, [...pins, { id: nextPinId(pins), label: `Choice ${pins.length + 1}` }]);
    },
    rename(node, pin, label) {
      const pins = pinsOf(node);
      const words = label.trim();
      if (!pins.some((p) => p.id === pin && p.label !== words)) return Promise.resolve(true);
      return setPins(node, pins.map((p) => (p.id === pin ? { ...p, label: words } : p)));
    },
    async wire(node, pin, to) {
      const from = nodeOf(node);
      const target = nodeOf(to);
      if (!from || !target) return false;
      const kind = kinds.wire(from.kind, target.kind, play.def()?.choices ?? []);
      if (!kind) {
        toast("No kind of link is offered here: turn on a plugin that brings some.", "error");
        return false;
      }
      const old = wireFrom(graph.state.graph.links, node, pin);
      if (old?.to === to) return true;
      const changes = old ? await actions.updateLink(old, { to, kind }) : await kinds.addLink({ from: node, to, kind, label: "", pin });
      if (!changes) return false;
      view.showLinkKind(kind);
      return true;
    },
    async disconnect(node, pin) {
      const old = wireFrom(graph.state.graph.links, node, pin);
      return !old || !!(await actions.removeLink(old));
    },
    remove(node, pin) {
      return setPins(node, pinsOf(node).filter((p) => p.id !== pin));
    },
  };
  return edits;
}
